import { Trans, useLingui } from '@lingui/react/macro';

import FileUpload from '@/components/FileUpload';
import { getCustomsFile, getCustomsValue } from '@/models/article';
import { hasPermission } from '@/models/user';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { useCurrentSession } from '@/services/auth';
import { useCountries } from '@/services/i18n';
import { formatCurrency } from '@/utils/number';

import { Item } from './Item';

export const ArticleCustomsDetails = () => {
  const { view: requestView } = useRequestContext();
  const { article } = useArticleContext();
  const { t } = useLingui();
  const countries = useCountries();
  const { currentSession } = useCurrentSession();

  const madein = article.product?.data.madein || article.data.madein;
  const customsValue = getCustomsValue(article);
  const customsFile = getCustomsFile(article);

  const canEditArticleCustomsInfo =
    hasPermission(currentSession, 'edit_customs_data', [
      {
        organizationId: article.organizationId,
      },
      article.workshopId
        ? {
            entityId: article.workshopId,
          }
        : null,
    ]) && !article.archivedAt;

  if (requestView.customs?.type === 'fields') {
    return (
      <>
        <Item
          label={t({
            id: 'article.information.customs.tariff-code',
            message: 'Tariff code',
          })}
          value={article.product?.data.tariffCode || article.data.tariffCode}
          error={
            canEditArticleCustomsInfo &&
            !(article.product?.data.tariffCode || article.data.tariffCode)
          }
        />
        <Item
          label={t({ id: 'article.information.customs.made-in', message: 'Made in' })}
          value={countries.find((country) => country.code === madein)?.name ?? '-'}
          error={canEditArticleCustomsInfo && !countries.find((country) => country.code === madein)}
        />
        <Item
          label={t({
            id: 'article.information.customs.manufacturer-code',
            message: 'Manufacturer code (MID)',
          })}
          value={article.product?.data.mid || article.data.mid}
          error={canEditArticleCustomsInfo && !(article.product?.data.mid || article.data.mid)}
        />
        <Item
          label={t({
            id: 'article.information.customs.customs-value',
            message: 'Customs value',
          })}
          value={customsValue ? formatCurrency(customsValue.amount, customsValue.currency) : '-'}
          error={canEditArticleCustomsInfo && !customsValue}
        />
      </>
    );
  }

  if (requestView.customs?.type === 'file') {
    return customsFile ? (
      <FileUpload
        ariaLabel={t({
          id: 'article.information.customs.file.title',
          message: 'Customs document',
        })}
        label={
          <p className="paragraph-200-regular">
            <Trans id="article.information.customs.file.title">Customs document</Trans>
          </p>
        }
        media={[customsFile]}
        uploadData={{
          type: 'customs-file',
          articleId: article?.id,
        }}
        style={{ flex: '1' }}
        size="xlarge"
        deleteWithApi
        secondaryActionIconOnly
        maxNumberOfMedia={1}
        variant="grid"
        withQrCode
        type="file"
        disabled
      />
    ) : (
      <Item
        label={t({
          id: 'article.information.customs.file.title',
          message: 'Customs document',
        })}
        value={'-'}
        error={canEditArticleCustomsInfo && !customsFile}
      />
    );
  }

  return null;
};
